import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemDecorator,
  ListItemContent,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Textarea,
  Stepper,
  Step,
  StepIndicator,
} from '@mui/joy';
import {
  CheckCircle as CheckIcon,
  XCircle as ErrorIcon,
  Warning as WarningIcon,
  Rocket as DeployIcon,
  ArrowLeft as BackIcon,
  ArrowRight as NextIcon,
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface WizardStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

interface ValidationCheck {
  id: string;
  name: string;
  status: 'pass' | 'fail' | 'warning';
  message?: string;
}

export const DeploymentWizard: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [deploymentConfig, setDeploymentConfig] = useState({
    environment: 'staging' as 'staging' | 'production',
    backup: true,
    notify: true,
    cache_invalidation: true,
    auto_rollback: true,
    notes: '',
  });

  const steps: WizardStep[] = [
    { id: 'validation', title: 'Pre-flight Validation', description: 'Check site readiness for deployment', completed: false },
    { id: 'environment', title: 'Environment Selection', description: 'Choose deployment target', completed: false },
    { id: 'options', title: 'Deployment Options', description: 'Configure deployment settings', completed: false },
    { id: 'review', title: 'Review & Deploy', description: 'Confirm and start deployment', completed: false },
  ];

  // Mock validation data
  const validationChecks: ValidationCheck[] = [
    { id: 'pages', name: 'Site has published pages', status: 'pass' },
    { id: 'domain', name: 'Domain configuration valid', status: 'pass' },
    { id: 'ssl', name: 'SSL certificate ready', status: 'warning', message: 'Certificate will be auto-generated' },
    { id: 'content', name: 'All content validated', status: 'pass' },
  ];

  // Deploy mutation
  const deployMutation = useMutation({
    mutationFn: async (config: typeof deploymentConfig) => {
      const response = await api.post(`/api/cms/sites/${siteId}/deploy/`, config);
      return response.data;
    },
    onSuccess: (data: any) => {
      navigate(`/cms/sites/${siteId}/deploy/progress/${data.deployment_id}`);
    },
  });

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDeploy = async () => {
    await deployMutation.mutateAsync(deploymentConfig);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Validation
        return validationChecks.every(check => check.status !== 'fail');
      case 1: // Environment
        return deploymentConfig.environment !== undefined;
      case 2: // Options
        return true;
      case 3: // Review
        return true;
      default:
        return false;
    }
  };

  const renderValidationStep = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography level="h4" mb={3}>Pre-flight Validation</Typography>
        <List>
          {validationChecks.map((check) => (
            <ListItem key={check.id}>
              <ListItemDecorator>
                {check.status === 'pass' && <CheckIcon color="var(--joy-palette-success-500)" />}
                {check.status === 'fail' && <ErrorIcon color="var(--joy-palette-danger-500)" />}
                {check.status === 'warning' && <WarningIcon color="var(--joy-palette-warning-500)" />}
              </ListItemDecorator>
              <ListItemContent>
                <Typography level="body-md">{check.name}</Typography>
                {check.message && (
                  <Typography level="body-sm" color="neutral">
                    {check.message}
                  </Typography>
                )}
              </ListItemContent>
            </ListItem>
          ))}
        </List>
        
        {validationChecks.some(check => check.status === 'fail') && (
          <Alert color="danger" sx={{ mt: 2 }}>
            <Typography level="title-sm">Validation Failed</Typography>
            <Typography level="body-sm">
              Please fix the issues above before proceeding with deployment.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderEnvironmentStep = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography level="h4" mb={3}>Select Deployment Environment</Typography>
        <FormControl>
          <FormLabel>Target Environment</FormLabel>
          <RadioGroup
            value={deploymentConfig.environment}
            onChange={(e) => setDeploymentConfig({ ...deploymentConfig, environment: e.target.value as 'staging' | 'production' })}
          >
            <Radio value="staging" label="Staging" />
            <Box ml={4} mb={2}>
              <Typography level="body-sm" color="neutral">
                Deploy to staging environment for testing and review
              </Typography>
            </Box>
            
            <Radio value="production" label="Production" />
            <Box ml={4}>
              <Typography level="body-sm" color="neutral">
                Deploy to live production environment (visible to users)
              </Typography>
            </Box>
          </RadioGroup>
        </FormControl>
        
        {deploymentConfig.environment === 'production' && (
          <Alert color="warning" sx={{ mt: 3 }}>
            <Typography level="title-sm">Production Deployment</Typography>
            <Typography level="body-sm">
              You are about to deploy to production. Make sure you've tested your changes in staging first.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderOptionsStep = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography level="h4" mb={3}>Deployment Options</Typography>
        <Stack spacing={3}>
          <FormControl>
            <Checkbox
              checked={deploymentConfig.backup}
              onChange={(e) => setDeploymentConfig({ ...deploymentConfig, backup: e.target.checked })}
              label="Create backup before deployment"
            />
            <Typography level="body-sm" color="neutral" sx={{ ml: 4 }}>
              Recommended: Creates a backup that can be restored if needed
            </Typography>
          </FormControl>
          
          <FormControl>
            <Checkbox
              checked={deploymentConfig.cache_invalidation}
              onChange={(e) => setDeploymentConfig({ ...deploymentConfig, cache_invalidation: e.target.checked })}
              label="Invalidate CDN cache"
            />
            <Typography level="body-sm" color="neutral" sx={{ ml: 4 }}>
              Clears cached content to ensure users see latest changes
            </Typography>
          </FormControl>
          
          <FormControl>
            <Checkbox
              checked={deploymentConfig.auto_rollback}
              onChange={(e) => setDeploymentConfig({ ...deploymentConfig, auto_rollback: e.target.checked })}
              label="Auto-rollback on failure"
            />
            <Typography level="body-sm" color="neutral" sx={{ ml: 4 }}>
              Automatically restore previous version if deployment fails
            </Typography>
          </FormControl>
          
          <FormControl>
            <Checkbox
              checked={deploymentConfig.notify}
              onChange={(e) => setDeploymentConfig({ ...deploymentConfig, notify: e.target.checked })}
              label="Send notification emails"
            />
            <Typography level="body-sm" color="neutral" sx={{ ml: 4 }}>
              Email notifications when deployment completes
            </Typography>
          </FormControl>
          
          <FormControl>
            <FormLabel>Deployment Notes (Optional)</FormLabel>
            <Textarea
              placeholder="Add any notes about this deployment..."
              value={deploymentConfig.notes}
              onChange={(e) => setDeploymentConfig({ ...deploymentConfig, notes: e.target.value })}
              minRows={3}
            />
          </FormControl>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderReviewStep = () => (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Typography level="h4" mb={3}>Deployment Summary</Typography>
          <Grid container spacing={2}>
            <Grid xs={6}>
              <Typography level="body-sm" fontWeight="lg">Environment:</Typography>
              <Chip color={deploymentConfig.environment === 'production' ? 'danger' : 'primary'}>
                {deploymentConfig.environment}
              </Chip>
            </Grid>
            <Grid xs={6}>
              <Typography level="body-sm" fontWeight="lg">Options:</Typography>
              <Stack spacing={0.5}>
                {deploymentConfig.backup && <Chip size="sm" variant="soft">Backup enabled</Chip>}
                {deploymentConfig.cache_invalidation && <Chip size="sm" variant="soft">Cache invalidation</Chip>}
                {deploymentConfig.auto_rollback && <Chip size="sm" variant="soft">Auto-rollback</Chip>}
                {deploymentConfig.notify && <Chip size="sm" variant="soft">Email notifications</Chip>}
              </Stack>
            </Grid>
          </Grid>
          
          {deploymentConfig.notes && (
            <Box mt={2}>
              <Typography level="body-sm" fontWeight="lg">Notes:</Typography>
              <Typography level="body-sm">{deploymentConfig.notes}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Alert color="primary">
        <Typography level="title-sm">Ready to Deploy</Typography>
        <Typography level="body-sm">
          Review the settings above and click "Start Deployment" when ready.
        </Typography>
      </Alert>
    </Stack>
  );

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography level="h2">Deployment Wizard</Typography>
          <Typography level="body-lg" color="neutral">
            Step-by-step deployment configuration
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startDecorator={<BackIcon />}
          onClick={() => navigate(`/cms/sites/${siteId}/deploy`)}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Stepper */}
      <Box mb={4}>
        <Stepper>
          {steps.map((step, index) => (
            <Step
              key={step.id}
              indicator={
                <StepIndicator
                  variant={index <= currentStep ? 'solid' : 'outlined'}
                  color={index < currentStep ? 'success' : index === currentStep ? 'primary' : 'neutral'}
                >
                  {index < currentStep ? <CheckIcon /> : index + 1}
                </StepIndicator>
              }
            >
              <Typography level="title-sm">{step.title}</Typography>
              <Typography level="body-xs">{step.description}</Typography>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step Content */}
      <Box mb={4}>
        {currentStep === 0 && renderValidationStep()}
        {currentStep === 1 && renderEnvironmentStep()}
        {currentStep === 2 && renderOptionsStep()}
        {currentStep === 3 && renderReviewStep()}
      </Box>

      {/* Navigation */}
      <Box display="flex" justifyContent="space-between">
        <Button
          variant="outlined"
          color="primary"
          onClick={handlePrevious}
          disabled={currentStep === 0}
          startDecorator={<BackIcon />}
        >
          Previous
        </Button>
        
        <Box display="flex" gap={2}>
          {currentStep < steps.length - 1 ? (
            <Button
              variant="solid"
              onClick={handleNext}
              disabled={!canProceed()}
              endDecorator={<NextIcon />}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="solid"
              color="success"
              onClick={handleDeploy}
              disabled={!canProceed()}
              loading={deployMutation.isPending}
              startDecorator={<DeployIcon />}
            >
              Start Deployment
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};